import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Alert,
    CircularProgress
} from '@mui/material';
import { Add, Edit, Delete, Inventory, Info } from '@mui/icons-material';
import { profilesApi, Profile } from '../services/profilesApi';
import { MaterialType } from '../services/windowsApi';
import GenericPage from '../../Commun/components/GenericPage';
import { ProfileEditDialog } from '../components/ProfileEditDialog';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';

const Profiles: React.FC = () => {
    const { t } = useLanguage();
    
    const { getParam } = useSystemParameters();
    const currencySymbol = getParam('Currency_Symbol', 'DZD');
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [profileToDelete, setProfileToDelete] = useState<number | null>(null);

    const loadProfiles = useCallback(async () => {
        setLoading(true);
        try {
            const response = await profilesApi.getAll();
            setProfiles(response.data || []);
        } catch (err) {
            console.error("Error loading profiles", err);
            blobticMessage.error("Error", t('maker.profiles.error.loading') || "Failed to load profiles");
        } finally {
            setLoading(false);
        }
    }, [blobticMessage.error, t]);

    useEffect(() => {
        loadProfiles();
    }, [loadProfiles]);

    const handleOpen = (profile?: Profile) => {
        setEditingProfile(profile || null);
        setOpen(true);
    };

    const handleDeleteClick = (id: number) => {
        setProfileToDelete(id);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (profileToDelete) {
            try {
                const result = await profilesApi.delete(profileToDelete);
                if (result.success) {
                    blobticMessage.success("Success", result.message || t('common.messages.deleted_success'));
                    loadProfiles();
                } else {
                    blobticMessage.error("Error", result.message || t('common.messages.delete_error'));
                }
            } catch (err: any) {
                console.error("Error deleting profile", err);
                blobticMessage.error("Error", err.message || t('common.messages.delete_error'));
            } finally {
                setDeleteConfirmOpen(false);
                setProfileToDelete(null);
            }
        }
    };

    const handleSaveSuccess = () => {
        loadProfiles();
        setOpen(false);
        blobticMessage.success("Success", t('common.messages.saved'));
    };

    const getMaterialTypeName = (type: number) => {
        switch (type) {
            case MaterialType.Aluminum: return t('maker.materials.type.aluminum');
            case MaterialType.PVC: return t('maker.materials.type.pvc');
            case MaterialType.Wood: return t('maker.materials.type.wood');
            case MaterialType.Steel: return t('maker.materials.type.steel');
            default: return t('maker.materials.type.unknown');
        }
    };

    return (
        <GenericPage
            title={t('maker.profiles.title')}
            icon={<Inventory />}
            footerEnabled={false}
            additionalActions={
                <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
                    {t('maker.profiles.add')}
                </Button>
            }
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>

                <Alert severity="info" icon={<Info />}>
                    <Typography variant="body2">
                        {t('maker.profiles.description')}
                    </Typography>
                </Alert>

                <TableContainer component={Paper} sx={{ flexGrow: 1, overflow: 'auto', boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('maker.profiles.table.name')}</TableCell>
                                <TableCell>{t('maker.profiles.table.code')}</TableCell>
                                <TableCell>{t('maker.profiles.table.material')}</TableCell>
                                <TableCell>{t('maker.profiles.table.dimensions')}</TableCell>
                                <TableCell align="right">{t('maker.profiles.table.price')}</TableCell>
                                <TableCell align="right">{t('maker.profiles.table.stock')}</TableCell>
                                <TableCell align="center">{t('maker.profiles.table.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : profiles.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">{t('maker.profiles.empty')}</TableCell>
                                </TableRow>
                            ) : (
                                profiles.map((profile) => (
                                    <TableRow key={profile.id} hover>
                                        <TableCell>{profile.name}</TableCell>
                                        <TableCell>{profile.code}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getMaterialTypeName(profile.materialType)}
                                                size="small"
                                                color={profile.materialType === MaterialType.Aluminum ? 'primary' : 'default'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{`${profile.width} x ${profile.height} x ${profile.depth} mm`}</TableCell>
                                        <TableCell align="right">
                                            {`${currencySymbol}${profile.unitPrice.toFixed(2)}`}
                                        </TableCell>
                                        <TableCell align="right">{profile.stockQuantity}</TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" color="primary" onClick={() => handleOpen(profile)}>
                                                <Edit />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(profile.id)}>
                                                <Delete />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <ProfileEditDialog
                    open={open}
                    onClose={() => setOpen(false)}
                    onSuccess={handleSaveSuccess}
                    profile={editingProfile}
                />

                <RootDialogPage
                    open={deleteConfirmOpen}
                    onClose={() => setDeleteConfirmOpen(false)}
                    title={t('maker.profiles.delete.title')}
                    onAction={handleDeleteConfirm}
                    actionLabel={t('maker.profiles.delete.action')}
                    maxWidth="xs"
                >
                    <Typography>
                        {t('maker.profiles.delete.confirm')}
                    </Typography>
                </RootDialogPage>
            </Box>
        </GenericPage>
    );
};

export default Profiles;
